/**
 * Routes. Signed out → sign in (plus the public pages). Signed in → first the password
 * change and authenticator enrolment if due, then the user's area: the LGU office, the
 * merchant portal (store owners), or the POS (cashiers; owners can open it too).
 */
import { lazy, Suspense } from 'react';
import { Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { Spinner } from '../components/ui';
import { useAuth } from '../lib/auth';
import { MfaSetup, SetPassword, SignIn } from './public/SignIn';
import { CardLanding } from './public/CardLanding';

const OfficeApp = lazy(() => import('./office/OfficeApp').then((m) => ({ default: m.OfficeApp })));
const MerchantApp = lazy(() => import('./merchant/MerchantApp').then((m) => ({ default: m.MerchantApp })));
const PosApp = lazy(() => import('./pos/PosApp').then((m) => ({ default: m.PosApp })));
const MerchantSignUp = lazy(() => import('./public/MerchantSignUp').then((m) => ({ default: m.MerchantSignUp })));
const Directory = lazy(() => import('./public/Directory').then((m) => ({ default: m.Directory })));
const Privacy = lazy(() => import('./shared/Privacy').then((m) => ({ default: m.Privacy })));

export function App(): JSX.Element {
  const { user, loading, area } = useAuth();
  const location = useLocation();
  if (loading) return <div className="grid min-h-dvh place-items-center"><Spinner /></div>;

  const home = area === 'office' ? '/office' : area === 'merchant' ? '/merchant' : '/pos';
  return (
    <Suspense fallback={<Spinner />}>
      <Routes>
        {/* Public: what a phone shows when someone taps a card; the merchant list; privacy. */}
        <Route path="/t" element={<CardLanding />} />
        <Route path="/directory" element={<Directory />} />
        <Route path="/privacy" element={<Privacy />} />
        {!user ? (
          <>
            <Route path="/sign-in" element={<SignIn />} />
            <Route path="/merchant-signup" element={<MerchantSignUp />} />
            <Route path="*" element={<Navigate to={`/sign-in?next=${encodeURIComponent(location.pathname + location.search)}`} replace />} />
          </>
        ) : user.mustChangePassword ? (
          <Route path="*" element={<SetPassword />} />
        ) : user.mfaRequired && !user.mfaEnabled ? (
          <Route path="*" element={<MfaSetup />} />
        ) : (
          <>
            {area === 'office' && <Route path="/office/*" element={<OfficeApp />} />}
            {area === 'merchant' && <Route path="/merchant/*" element={<MerchantApp />} />}
            {(area === 'merchant' || area === 'pos') && <Route path="/pos/*" element={<PosApp />} />}
            <Route path="/sign-in" element={<Navigate to={safeNext(location.search) ?? home} replace />} />
            <Route path="*" element={<Navigate to={home} replace />} />
          </>
        )}
      </Routes>
    </Suspense>
  );
}

/** Only in-app paths, never another site. */
function safeNext(search: string): string | null {
  const next = new URLSearchParams(search).get('next');
  return next && next.startsWith('/') && !next.startsWith('//') ? next : null;
}
